import { initAppWorkspace } from './demo-app-workspace.mjs';
import { initWebsiteMotion } from './demo-website-motion.mjs';

/** A deliberately isolated demo: no fetch, storage, cookies or real form submission. */
export function initDemoStudio(onLayout = () => {}) {
  const root = document.querySelector('[data-demo-studio]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';
  const one = s => root.querySelector(s);
  const all = s => [...root.querySelectorAll(s)];
  let activeDemo = 'website';
  const tabs = all('[data-demo-tab]');
  const form = one('[data-demo-enquiry]');
  let layoutFrame;
  const layout = () => {
    cancelAnimationFrame(layoutFrame);
    layoutFrame = requestAnimationFrame(onLayout);
  };
  const el = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const setPressed = (selector, attr, value) => all(selector).forEach(b => b.setAttribute('aria-pressed', String(b.dataset[attr] === value)));
  const setSitePage = (page, moveFocus = false) => {
    all('[data-site-view]').forEach(p => { p.hidden = p.dataset.siteView !== page; });
    setPressed('[data-site-page]', 'sitePage', page);
    if (moveFocus) {
      const heading = one(`[data-site-view="${page}"] h3`);
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    layout();
  };
  const setPreview = size => {
    one('[data-demo-site]').dataset.preview = size;
    setPressed('[data-preview-size]', 'previewSize', size);
    layout();
  };
  const activateDemo = name => {
    activeDemo = name;
    tabs.forEach(t => {
      const active = t.dataset.demoTab === name;
      t.setAttribute('aria-selected', String(active));
      t.tabIndex = active ? 0 : -1;
    });
    all('[data-demo-panel]').forEach(p => { p.hidden = p.dataset.demoPanel !== name; });
    layout();
  };

  // Reuse the same three native disclosure rows on the Services page.
  one('[data-service-list]').replaceChildren(...all('.demo-service-index details').map(d => d.cloneNode(true)));

  root.addEventListener('click', event => {
    const button = event.target.closest('button');
    if (!button || !root.contains(button)) return;
    const data = button.dataset;
    if (data.demoTab) activateDemo(data.demoTab);
    if (data.sitePage || data.siteGo) setSitePage(data.sitePage || data.siteGo, Boolean(data.siteGo));
    if (data.previewSize) setPreview(data.previewSize);
    if (data.demoReset === 'website') {
      form.reset();
      all('[data-demo-enquiry] [aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
      one('[data-demo-form-result]').textContent = '';
      one('[data-demo-form-result]').classList.remove('is-error');
      all('.demo-site details').forEach(d => { d.open = false; });
      setSitePage('home'); setPreview('desktop');
    }

  });
  one('[data-demo-tabs]').addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const current = tabs.findIndex(t => t.dataset.demoTab === activeDemo);
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    activateDemo(tabs[index].dataset.demoTab); tabs[index].focus({ preventScroll: true });
  });
  root.addEventListener('toggle', layout, true);
  form.addEventListener('submit', event => {
    event.preventDefault();
    const fields = [...form.querySelectorAll('input, textarea')];
    fields.forEach(field => field.setAttribute('aria-invalid', String(!field.validity.valid || !field.value.trim())));
    const invalid = fields.find(field => field.getAttribute('aria-invalid') === 'true');
    const result = one('[data-demo-form-result]');
    result.classList.toggle('is-error', Boolean(invalid));
    if (invalid) {
      result.textContent = `Please enter ${invalid.type === 'email' ? 'a valid email address' : invalid.name === 'demo_name' ? 'a name' : 'a message'}. Nothing has been sent.`;
      invalid.focus();
    } else result.textContent = 'Sample enquiry complete. Nothing was sent or saved.';
    layout();
  });
  form.addEventListener('input', event => {
    if (event.target.matches('input, textarea')) event.target.removeAttribute('aria-invalid');
    one('[data-demo-form-result]').textContent = '';
  });
  // Resize only requests layout refresh; never mutates the demo's state.
  const observer = new ResizeObserver(layout);
  observer.observe(one('[data-demo-frame]'));
  initAppWorkspace(one('[data-enterprise-app]'), layout);
  all('[data-demo-enhanced], [data-demo-tabs]').forEach(node => { node.hidden = false; });
  activateDemo('website');
  // Real Solutions-page deep links select the right demo without resetting data.
  const selectLinkedDemo = (name, scroll = false) => {
    if (!['website', 'app'].includes(name)) return;
    activateDemo(name);
    if (scroll) root.scrollIntoView({ block: 'start', behavior: 'auto' });
  };
  const readDemoHash = () => {
    const name = { '#website-demo': 'website', '#app-demo': 'app' }[window.location.hash];
    if (name) requestAnimationFrame(() => selectLinkedDemo(name, true));
  };
  document.querySelectorAll('[data-demo-link]').forEach(link => link.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState(null, '', link.getAttribute('href'));
    selectLinkedDemo(link.dataset.demoLink, true);
  }));
  window.addEventListener('hashchange', readDemoHash);
  window.addEventListener('popstate', readDemoHash);
  window.addEventListener('load', readDemoHash, { once: true });
  readDemoHash();
  initWebsiteMotion(one('[data-demo-site]'));
}
